import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { PageHeading } from "@via/components/page-heading.tsx";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import Table from "@via/components/table/table.tsx";
import HowItWorksHelp from "@via/components/how-it-works/how-it-works-help";
import useUrlParams from "@via/hooks/useURLParams";
import {
  PAGE_NAMES,
  SAMPLE_TRACKERS_TABLE_OPTIONS,
  BUTTONS,
  ROUTES,
} from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { EMPTY_TABLE_MESSAGE, ERROR } from "@via/utils/constants.tsx";
import { type VmetaProject } from "common";
import {
  useVmetaProjects,
  useDeleteVmetaCanvas,
  useCheckVmetaCanvasWritePermission,
  type CheckVmetaCanvasWritePermissionResponseBody,
} from "@via/pages/projects/projects.api.tsx";
import SampleTrackersFilter from "@via/pages/sample-trackers/sample-trackers-filter.tsx";
import { useCallback, useEffect, useState, type JSX } from "react";
import { Link, useNavigate } from "react-router-dom";
import TableContextMenu from "@via/components/table/table-context-menu";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { ActionButton } from "@via/components/action-button/action-button";
import Drawer from "@via/components/drawer";
import { DrawerSize } from "@via/components/table/table";
import CreateSampleTrackerForm from "@via/pages/sample-trackers/components/create-sample-tracker-form";
import EditSampleTrackerForm from "@via/pages/sample-trackers/components/edit-sample-tracker-form";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useAuth } from "@via/hooks/useAuth";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

type SampleTrackerType = VmetaProject & {
  owner: {
    name: string;
  };
  createdAt: Date;
  updatedAt: Date;
};

const columnHelper = createColumnHelper<SampleTrackerType>();

const defaultParams = {
  sort: [{ id: "updatedAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

export default function SampleTrackers(): JSX.Element {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialParams = getInitialParams();
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);
  const [sampleTrackerViewType, setSampleTrackerViewType] =
    useState<SampleTrackerViewType>(SampleTrackerViewType.My);
  const [isCreateSampleTrackerDrawerOpen, setIsCreateSampleTrackerDrawerOpen] =
    useState(false);
  const [isEditSampleTrackerDrawerOpen, setIsEditSampleTrackerDrawerOpen] =
    useState(false);
  const [sampleTrackerToBeEdited, setSampleTrackerToBeEdited] =
    useState<SampleTrackerType | null>(null);
  const [sampleTrackerToBeDeleted, setSampleTrackerToBeDeleted] =
    useState<SampleTrackerType | null>(null);
  const [deletePermissionResult, setDeletePermissionResult] =
    useState<CheckVmetaCanvasWritePermissionResponseBody | null>(null);

  const { isLoading, isError, data } = useVmetaProjects(
    page,
    pageSize,
    sort,
    columnFilters,
    true,
    sampleTrackerViewType,
    user?.vmeta?.userId
  );

  const {
    mutateAsync: deleteVmetaCanvas,
    isPending: isDeleteVmetaCanvasLoading,
  } = useDeleteVmetaCanvas();

  const {
    mutateAsync: checkCanvasWritePermission,
    isPending: isCheckCanvasWritePermissionLoading,
  } = useCheckVmetaCanvasWritePermission();

  const getSampleTrackersContext = useCallback(() => {
    const trackers = (data?.data || []) as SampleTrackerType[];

    return {
      sampleTrackers: trackers.map(tracker => ({
        id: tracker._id,
        name: tracker.name,
        label: tracker.label,
        owner: tracker.owner?.name,
        createdAt: tracker.createdAt,
        updatedAt: tracker.updatedAt,
      })),
      total: data?.results || 0,
      currentPage: page,
      pageSize: pageSize,
      filters: columnFilters,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [data?.data, data?.results, columnFilters, page, pageSize, sort]);

  useRegisterCortexContext({
    routePattern: ROUTES.SAMPLE_TRACKERS,
    contextName: "Sample Trackers",
    getContext: getSampleTrackersContext,
    systemPrompt:
      "You are analyzing sample trackers list page. Help answer questions about the sample trackers list page.",
  });

  const handleAction = (
    option: TableContextMenuInterface,
    sampleTracker: SampleTrackerType
  ): void => {
    switch (option.name) {
      case "View":
        navigate(`/sample-trackers/${sampleTracker._id}`);
        break;
      case "Edit":
        // Permission is checked before rendering options (via writeAllowed on list response).
        setSampleTrackerToBeEdited(sampleTracker);
        setIsEditSampleTrackerDrawerOpen(true);
        break;
      case "Delete":
        setSampleTrackerToBeDeleted(sampleTracker);
        setDeletePermissionResult(null);
        break;
      default:
        break;
    }
  };

  const getRowOptions = useCallback(
    (sampleTracker: SampleTrackerType): TableContextMenuInterface[] => {
      const canWrite = !!sampleTracker.writeAllowed;
      return SAMPLE_TRACKERS_TABLE_OPTIONS.filter(opt => {
        if (opt.name === "Edit" || opt.name === "Delete") return canWrite;
        return true; // View
      });
    },
    []
  );

  useEffect(() => {
    if (!sampleTrackerToBeDeleted?._id) return;

    (async () => {
      try {
        const res = await checkCanvasWritePermission({
          id: String(sampleTrackerToBeDeleted._id),
        });
        setDeletePermissionResult(res);
      } catch (e) {
        // If check fails, fall back to showing regular delete confirm
        setDeletePermissionResult(null);
        console.error("Error checking canvas write permission:", e);
      }
    })();
  }, [sampleTrackerToBeDeleted?._id, checkCanvasWritePermission]);

  const columns = [
    columnHelper.accessor("name", {
      cell: info => {
        return (
          <Link to={info.row.original._id.toString()} className="via-link">
            {info.getValue()}
          </Link>
        );
      },
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("label", {
      cell: info => info.getValue(),
      header: () => <span>Label</span>,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Date Created</span>,
    }),
    columnHelper.accessor("updatedAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Last Updated</span>,
    }),
    columnHelper.accessor("owner.name", {
      cell: info => info.getValue(),
      header: () => <span>Owner</span>,
    }),
    columnHelper.accessor("_id", {
      cell: info => {
        return (
          <TableContextMenu
            options={getRowOptions(info.row.original)}
            onSelect={option => {
              handleAction(option, info.row.original);
            }}
          />
        );
      },
      header: () => "Options",
      enableSorting: false,
    }),
  ] as Array<ColumnDef<SampleTrackerType>>;

  useEffect(() => {
    setUrlParams({
      sort,
      filters: columnFilters,
      page,
      pageSize,
    });
  }, [sort, columnFilters, page, pageSize]);

  return (
    <main className="via-content-wrapper">
      <div className="via-page-heading-wrapper">
        <PageHeading title={PAGE_NAMES.SAMPLE_TRACKERS} />
        <div className="flex items-center gap-2">
          <HowItWorksHelp
            title={
              <div className="flex items-center gap-2 pr-10">
                <InformationCircleIcon className="h-5 w-5 text-blue-700" />
                <span className="text-xl font-semibold text-gray-900">
                  Sample Tracker
                </span>
              </div>
            }>
            <div>
              <p className="mt-4 text-base leading-6 text-gray-700">
                Sample Trackers help you organize experiment data and file
                information in one place.
              </p>

              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                How it works
              </h3>

              <div className="mt-3 space-y-4 text-base leading-6 text-gray-900">
                <div>
                  <div className="font-semibold">Collections</div>
                  <div className="mt-1 text-gray-700">
                    A Sample Tracker is made of collections—each one is a list
                    of items with fields you define (like a simple table).
                  </div>
                </div>

                <div>
                  <div className="font-semibold">
                    Files collection (created for you)
                  </div>
                  <div className="mt-1 text-gray-700">
                    Every Sample Tracker includes a built-in{" "}
                    <span className="font-semibold">Files</span> collection with
                    common fields like Name, File1, File2, and GEO ID.
                  </div>
                </div>

                <div>
                  <div className="font-semibold">Add fields</div>
                  <div className="mt-1 text-gray-700">
                    Need to track more details? Use{" "}
                    <span className="font-semibold">Add fields</span> to add new
                    fields to the selected Sample Tracker.
                  </div>
                </div>

                <div>
                  <div className="font-semibold">Configure fields</div>
                  <div className="mt-1 text-gray-700">
                    To manage existing fields, open the Sample Tracker and go to{" "}
                    <span className="font-semibold">
                      Configuration → Fields
                    </span>
                    .
                  </div>
                </div>
              </div>
            </div>
          </HowItWorksHelp>

          <ActionButton
            button={BUTTONS.NEW_SAMPLE_TRACKER}
            onClick={() => {
              setIsCreateSampleTrackerDrawerOpen(true);
            }}
          />
        </div>
      </div>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          data={data?.data}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={data?.results}
          emptyMessage={EMPTY_TABLE_MESSAGE.PROJECTS}
          additionalFilters={
            <SampleTrackersFilter
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setPage}
              setPageSize={setPageSize}
              sampleTrackerViewType={sampleTrackerViewType}
              onSampleTrackerViewTypeChange={setSampleTrackerViewType}
            />
          }
        />
      )}
      <Drawer
        id="create-sample-tracker"
        open={isCreateSampleTrackerDrawerOpen}
        setOpen={setIsCreateSampleTrackerDrawerOpen}
        size={DrawerSize.Big}>
        <CreateSampleTrackerForm setOpen={setIsCreateSampleTrackerDrawerOpen} />
      </Drawer>
      <Drawer
        id="edit-sample-tracker"
        open={isEditSampleTrackerDrawerOpen}
        setOpen={setIsEditSampleTrackerDrawerOpen}
        size={DrawerSize.Big}>
        <EditSampleTrackerForm
          setOpen={setIsEditSampleTrackerDrawerOpen}
          sampleTracker={sampleTrackerToBeEdited}
        />
      </Drawer>
      {sampleTrackerToBeDeleted && (
        <DeleteModal
          item={sampleTrackerToBeDeleted}
          setItem={setSampleTrackerToBeDeleted}
          deleteFunction={() =>
            deleteVmetaCanvas(sampleTrackerToBeDeleted._id as string)
          }
          isLoading={
            isDeleteVmetaCanvasLoading || isCheckCanvasWritePermissionLoading
          }
          title="Delete Sample Tracker"
          confirmText="Delete Sample Tracker"
          hideConfirmButton={
            deletePermissionResult
              ? !deletePermissionResult.data.writeAllowed
              : false
          }
          message={name => {
            if (
              isCheckCanvasWritePermissionLoading &&
              !deletePermissionResult
            ) {
              return <>Checking permissions…</>;
            }

            if (
              deletePermissionResult &&
              !deletePermissionResult.data.writeAllowed
            ) {
              return (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                  <div className="mt-1">
                    You don&apos;t have permission to delete{" "}
                    <strong className="font-semibold">{name}</strong>.
                  </div>
                </div>
              );
            }

            const inUseDatasets = deletePermissionResult?.data?.inUseDatasets
              ? deletePermissionResult.data.inUseDatasets
              : [];
            const datasetNames = inUseDatasets.length
              ? Array.from(
                  new Set(
                    inUseDatasets.map(d => d.name || d.id).filter(Boolean)
                  )
                )
              : [];
            const datasetCount = datasetNames.length;

            return (
              <>
                Are you sure you want to delete{" "}
                <strong className="font-semibold">{name}</strong>?
                <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                  <div className="font-semibold">Warning</div>
                  <div className="mt-1">
                    Deleting <strong className="font-semibold">{name}</strong>{" "}
                    is permanent.
                  </div>

                  <div className="mt-3 font-semibold">What will be deleted</div>
                  <div className="mt-1">
                    Everything stored in this Sample Tracker.
                  </div>

                  <div className="mt-3 font-semibold">
                    What will be affected
                  </div>
                  {datasetCount > 0 ? (
                    <>
                      <div className="mt-1">
                        This Sample Tracker is referenced by{" "}
                        <strong className="font-semibold">
                          {datasetCount}
                        </strong>{" "}
                        datasets. If you delete it, file data will be removed
                        from those datasets.
                      </div>
                      <details className="mt-2">
                        <summary className="cursor-pointer select-none">
                          <span className="font-semibold">
                            Affected datasets ({datasetCount})
                          </span>{" "}
                          (expand to view list)
                        </summary>
                        <div className="mt-2 text-red-900">
                          {datasetNames.map(ds => (
                            <div key={ds}>{ds}</div>
                          ))}
                        </div>
                      </details>
                    </>
                  ) : (
                    <div className="mt-1">
                      No datasets currently reference this Sample Tracker.
                    </div>
                  )}
                </div>
              </>
            );
          }}
        />
      )}
    </main>
  );
}
